import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Three-dot actions menu for a connection row.
 * The menu is rendered into document.body, so no parent (Scrollbars, overflow:hidden,
 * transforms, stacking contexts) can clip or hide it.
 */
const MENU_W = 168;
const MENU_H = 92;

const RowMenu = ({ label, onEdit, onDelete }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    const toggle = () => {
        if (!open && btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            // open below the button; flip above if there is no room
            let top = r.bottom + 6;
            if (top + MENU_H > window.innerHeight - 8) top = Math.max(8, r.top - MENU_H - 6);
            // right-align to the button, keep inside the viewport
            const left = Math.min(Math.max(8, r.right - MENU_W), window.innerWidth - MENU_W - 8);
            setPos({ top, left });
        }
        setOpen((o) => !o);
    };

    useEffect(() => {
        if (!open) return undefined;

        const onPointerDown = (e) => {
            if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                setOpen(false);
                btnRef.current?.focus();
            }
        };
        const close = () => setOpen(false);

        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        window.addEventListener("resize", close);
        window.addEventListener("scroll", close, true); // any scroll container

        // move focus into the menu for keyboard users
        menuRef.current?.querySelector("button")?.focus();

        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("resize", close);
            window.removeEventListener("scroll", close, true);
        };
    }, [open]);

    const run = (fn) => () => {
        setOpen(false);
        fn();
    };

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                className={`sc-conn-menu-btn ${open ? "is-open" : ""}`}
                aria-label={`Actions for ${label}`}
                aria-haspopup="menu"
                aria-expanded={open}
                title="Actions"
                onClick={toggle}
            >
                <i className="fas fa-ellipsis-h" />
            </button>

            {open &&
                createPortal(
                    <div
                        ref={menuRef}
                        className="sc-rowmenu"
                        role="menu"
                        style={{ top: pos.top, left: pos.left, width: MENU_W }}
                        onKeyDown={(e) => {
                            // simple up/down navigation between the two items
                            if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
                            e.preventDefault();
                            const items = [...menuRef.current.querySelectorAll("button")];
                            const i = items.indexOf(document.activeElement);
                            const next = e.key === "ArrowDown" ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
                            items[next].focus();
                        }}
                    >
                        <button type="button" role="menuitem" className="sc-rowmenu-item" onClick={run(onEdit)}>
                            <span className="sc-rowmenu-icon is-edit"><i className="fas fa-pen" /></span>
                            Edit storage
                        </button>
                        <div className="sc-rowmenu-sep" />
                        <button type="button" role="menuitem" className="sc-rowmenu-item is-danger" onClick={run(onDelete)}>
                            <span className="sc-rowmenu-icon"><i className="fas fa-trash" /></span>
                            Delete
                        </button>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default RowMenu;